// Les réglages restent dans SotCP V3 (§11) : FlashCP les LIT, ne les écrit
// jamais. Un seul chargement au démarrage, gardé en mémoire — ils changent
// quelques fois par an, pas quelques fois par heure.

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { poserBandes } from '@/domain/ecart'

export interface Caissier {
  badge: number | string
  nom: string
  prenom: string
  secteur?: string
  droits?: string
  caisson?: number | null
  /** « AVEC ESPÈCES » / « SANS ESPÈCES » — pilote le fonds de caisse attendu. */
  fdc?: string
}

export interface CaisseFixe { id: string; name: string; default_amount: number }
export interface TypeBonDivers { value: string; label: string }

export interface MachineSco { caisse: number; monnayeur: number }

export interface ReglesScoReglage {
  seuil_pieces: number
  seuil_billets: number
  billets_initial: number
  billets_palier: number
  billets_recharges: number[]
  /** Bornes hautes de la bande orange — au-dessus, le stock est confortable. */
  vigilance_pieces?: number
  vigilance_billets?: number
}

/** Les bandes de couleur d'un écart, en euros. */
export interface BandesEcart { tolere: number; critique: number }

export interface Reglages {
  caissiers: Caissier[]
  caisseFixes: CaisseFixe[]
  typesBonDivers: TypeBonDivers[]
  badgesSansFdc: string[]
  badgesSuperviseur: string[]
  seuilDeclarationEspeces: number
  quotaInventaires: number
  /** Correspondance caisse ↔ monnayeur des caisses automatiques. */
  machinesSco: MachineSco[]
  /** Seuils et paliers de recharge des monnayeurs. */
  reglesSco: ReglesScoReglage | null
  /** En dessous de `tolere`, un écart est neutre ; au-delà de `critique`, il arrête. */
  bandesEcart: BandesEcart
  /** Causes proposées pour un écart de coffre — le texte libre reste ce qui fait foi. */
  causesEcartCoffre: string[]
  /** Motifs proposés pour mettre une étape de côté. */
  motifsDeCote: string[]
}

export const REGLAGES_DEFAUT: Reglages = {
  caissiers: [], caisseFixes: [], typesBonDivers: [],
  badgesSansFdc: [], badgesSuperviseur: [],
  seuilDeclarationEspeces: 650,   // seuil V2 de déclaration espèces
  quotaInventaires: 20,           // quota des 20 inventaires par mois
  machinesSco: [], reglesSco: null,
  bandesEcart: { tolere: 2, critique: 5 },
  causesEcartCoffre: [
    'Prélèvement GLORY non remonté',
    'Enveloppe de 500 € non décomptée',
    'Apport caissier saisi après le comptage',
    'Livraison de monnaie non enregistrée',
    'Erreur de comptage — recomptage fait',
    'Solde de la veille erroné',
  ],
  // « Autre » n'est pas dans la liste : c'est le code qui l'ajoute toujours en
  // dernier, parce qu'il ouvre la saisie libre. Le retirer d'un réglage
  // supprimerait le seul chemin vers un motif non prévu.
  motifsDeCote: [
    'En attente d’un retour (AIM, comptabilité)',
    'Fichier ou état manquant',
    'Interruption — je reprends plus tard',
  ],
}

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

const objetOuNull = <T,>(v: unknown): T | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as T) : null

const nombre = (v: unknown, defaut: number): number =>
  Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : defaut

/** Une liste vide veut dire « rien à proposer », pas « remets les défauts ». */
const textes = (v: unknown, defaut: string[]): string[] =>
  Array.isArray(v) ? v.map(String).filter((x) => x.trim() !== '') : defaut

let cache: Reglages | null = null
let enVol: Promise<Reglages> | null = null
/** Les abonnés à recharger quand un réglage change, ici ou sur un autre poste. */
const abonnes = new Set<(r: Reglages) => void>()
let canal: ReturnType<typeof supabase.channel> | null = null

/**
 * Jeter le cache et prévenir tous les écrans ouverts.
 *
 * Le cache était permanent : « un seul chargement au démarrage, ils changent
 * quelques fois par an ». C'était vrai tant que personne ne pouvait les changer
 * DEPUIS FlashCP. Un back-office sans invalidation ne verrait pas ses propres
 * écritures, et le poste d'à côté compterait le coffre avec l'ancien fonds fixe
 * pendant des heures — sans que rien ne le signale.
 */
export async function invaliderReglages(): Promise<Reglages> {
  cache = null
  enVol = null
  const frais = await chargerReglages()
  for (const f of abonnes) f(frais)
  return frais
}

export async function chargerReglages(): Promise<Reglages> {
  if (cache) return cache
  if (enVol) return enVol

  enVol = (async () => {
    const { data, error } = await supabase.from('settings').select('key, value')
    if (error) throw error
    const m = new Map((data ?? []).map((r) => [r.key as string, r.value as unknown]))
    const tab = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])
    const seuils = (m.get('thresholds') ?? {}) as Record<string, unknown>
    const quota = Number(m.get('tk_quota_inventaires'))

    cache = {
      caissiers: tab<Caissier>(m.get('caissiers')),
      caisseFixes: tab<CaisseFixe>(m.get('caisse_fixes')),
      typesBonDivers: tab<TypeBonDivers>(m.get('bon_divers_types')),
      badgesSansFdc: tab<string>(m.get('fdcZeroBadges')).map(String),
      badgesSuperviseur: tab<string>(m.get('superviseurBadges')).map(String),
      seuilDeclarationEspeces: Number(seuils.declaration_especes) > 0
        ? Number(seuils.declaration_especes) : REGLAGES_DEFAUT.seuilDeclarationEspeces,
      quotaInventaires: Number.isFinite(quota) && quota > 0 ? quota : REGLAGES_DEFAUT.quotaInventaires,
      machinesSco: tab<MachineSco>(m.get('fc_sco_machines')),
      // Cast VÉRIFIÉ : un objet malformé passerait sans bruit un simple `as`,
      // et chaque lecteur retomberait sur les défauts par accident.
      reglesSco: objetOuNull<ReglesScoReglage>(m.get('fc_sco_regles')),
      bandesEcart: {
        tolere: nombre(objet(m.get('fc_ecart_bandes')).tolere, REGLAGES_DEFAUT.bandesEcart.tolere),
        critique: nombre(objet(m.get('fc_ecart_bandes')).critique, REGLAGES_DEFAUT.bandesEcart.critique),
      },
      causesEcartCoffre: textes(m.get('fc_causes_ecart_coffre'), REGLAGES_DEFAUT.causesEcartCoffre),
      motifsDeCote: textes(m.get('fc_motifs_de_cote'), REGLAGES_DEFAUT.motifsDeCote),
    }
    // Les bornes de couleur d'un écart sont lues par une trentaine d'appels qui
    // ne reçoivent pas les réglages : on les pose ici, une fois.
    poserBandes(cache.bandesEcart)
    return cache
  })()

  try { return await enVol } finally { enVol = null }
}

export function useReglages(): Reglages {
  const [r, setR] = useState<Reglages>(cache ?? REGLAGES_DEFAUT)
  useEffect(() => {
    let vivant = true
    const poser = (x: Reglages) => { if (vivant) setR(x) }
    abonnes.add(poser)
    chargerReglages().then(poser).catch(() => {})

    // Un réglage changé depuis un autre poste doit arriver ici : deux postes qui
    // comptent le même coffre avec deux fonds fixes différents produiraient deux
    // écarts, et personne ne saurait lequel croire.
    if (!canal) {
      canal = supabase.channel('fc-settings')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'settings' },
            () => { void invaliderReglages().catch(() => {}) })
        .subscribe()
    }
    return () => { vivant = false; abonnes.delete(poser) }
  }, [])
  return r
}

export const nomCaissier = (c: Caissier): string => `${c.nom} ${c.prenom}`.trim()

/** Un caissier « SANS ESPÈCES » n'a pas de fonds de caisse à servir. */
export const aUnFondsDeCaisse = (c: Caissier, badgesSansFdc: string[]): boolean =>
  !badgesSansFdc.includes(String(c.badge)) && (c.fdc ?? '').toUpperCase() !== 'SANS ESPÈCES'
